import React from "react";
import appIcon from "../../assets/fitclub-app-icon.png";
import { classNames } from "../../helpers";
import "../../styles/onboarding.css";
import OnboardingUserMenu from "./OnboardingUserMenu";

/**
 * FitClub Pro logo: the iOS app icon (coral square with the runner) + wordmark, sized responsively.
 * variant "dark" = for light backgrounds, "light" = for the coffee brand panel (only the wordmark colour changes).
 */
export function BrandLogo({ variant = "dark", className = "" }) {
  const onDark = variant === "light";
  return (
    <div className={classNames("flex items-center gap-2 sm:gap-3", className)}>
      <img
        src={appIcon}
        alt=""
        className={classNames("shrink-0 rounded-[22%] w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14", onDark ? "shadow-lg" : "")}
      />
      <span className={classNames("font-extrabold tracking-tight text-xl sm:text-2xl lg:text-[26px]", onDark ? "text-white" : "text-brown-coffee")}>
        <span className="text-portland-orange">Fit</span>Club Pro
      </span>
    </div>
  );
}

const CheckMark = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M5 12l5 5L20 7" />
  </svg>
);

const rotatingLines = [
  "Members book a class in two taps.",
  "Payments land straight in your account.",
  "A members' app with your club's timetable.",
];

/**
 * Left-hand coffee panel of the sign-up screen (hidden on small screens).
 * Sign-in reuses it with its own title and line and without the trial badge or the feature ticks.
 */
export function OnboardingBrandPanel({ badge = "30 DAYS FREE · CANCEL ANYTIME", title = null, lines = rotatingLines, showFeatures = true }) {
  return (
    <section className="relative hidden lg:flex lg:w-[44%] xl:w-[42%] shrink-0 flex-col overflow-hidden bg-brown-coffee text-white px-12 xl:px-16 py-12">
      <div className="absolute -right-36 -top-32 h-[420px] w-[420px] rounded-full bg-light-brown-coffee" aria-hidden="true" />
      <div className="absolute -left-24 -bottom-20 h-64 w-64 rounded-full bg-portland-orange/20" aria-hidden="true" />

      <BrandLogo variant="light" className="relative" />

      <div className="relative mt-16 xl:mt-20">
        {badge && (
          <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-wide">
            <span className="h-2 w-2 rounded-full bg-[#7CE0A3]" />
            {badge}
          </div>
        )}
        <h1 className="text-4xl xl:text-[52px] font-black leading-[1.05] tracking-tight">
          {title || (<>Run your club.<br />Not your admin.</>)}
        </h1>
        {lines.length > 1 ? (
          <div className="mt-5 h-12 overflow-hidden" aria-live="off">
            <div className="fc-rotate3 flex flex-col">
              {lines.map((line) => (
                <p key={line} className="h-12 flex items-center text-lg xl:text-xl font-semibold text-[#F7C6CC]">{line}</p>
              ))}
            </div>
          </div>
        ) : (
          <p className="mt-5 text-lg xl:text-xl font-semibold text-[#F7C6CC]">{lines[0]}</p>
        )}
      </div>

      <div className="relative flex-1 min-h-[220px]" aria-hidden="true">
        <div className="fc-float absolute left-0 top-8 flex w-72 items-center gap-3 rounded-2xl bg-white p-4 text-gray-900 shadow-2xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F3E9FF]">
            <svg viewBox="0 0 24 24" fill="none" stroke="#7e00dd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" /></svg>
          </span>
          <span>
            <span className="block text-xs font-semibold text-gray-500">New booking</span>
            <span className="block text-base font-extrabold">Yoga Flow · Today 18:00</span>
          </span>
        </div>
        <div className="fc-float2 absolute left-40 xl:left-56 top-36 flex w-72 items-center gap-3 rounded-2xl bg-white p-4 text-gray-900 shadow-2xl">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#E8F7EE]">
            <svg viewBox="0 0 24 24" fill="none" stroke="#2F7A0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6"><path d="M4 12h16M14 6l6 6-6 6" /></svg>
          </span>
          <span>
            <span className="block text-xs font-semibold text-gray-500">Payout</span>
            <span className="block text-base font-extrabold">Paid out to your bank</span>
          </span>
        </div>
      </div>

      {showFeatures && (
        <div className="relative flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold text-[#E9DDE6]">
          {["Set up in minutes", "iDEAL, cards & Apple Pay", "Passes & subscriptions"].map((t) => (
            <span key={t} className="flex items-center gap-2"><CheckMark className="h-4 w-4 text-portland-orange" />{t}</span>
          ))}
        </div>
      )}
    </section>
  );
}

/** Top step indicator: Account -> Plan -> Your club */
export function OnboardingStepper({ current = 1 }) {
  const steps = ["Account", "Plan", "Your club", "Payouts"];
  return (
    <ol className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm font-bold" aria-label="Onboarding progress">
      {steps.map((label, i) => {
        const index = i + 1;
        const done = index < current;
        const active = index === current;
        return (
          <li key={label} className="flex items-center gap-2 sm:gap-3">
            {i > 0 && <span className={classNames("hidden sm:block h-0.5 w-6 lg:w-8 xl:w-10", done || active ? "bg-indigo-bz" : "bg-gray-300")} aria-hidden="true" />}
            <span className={classNames("flex items-center gap-2", active ? "text-indigo-bz" : done ? "text-gray-700" : "text-gray-400")}>
              <span
                className={classNames(
                  "flex h-6 w-6 items-center justify-center rounded-full text-[11px]",
                  done ? "bg-bz-status-green text-white" : active ? "bg-indigo-bz text-white" : "border-2 border-gray-300"
                )}
                aria-current={active ? "step" : undefined}
              >
                {done ? <CheckMark className="h-3.5 w-3.5" /> : index}
              </span>
              <span className={classNames("whitespace-nowrap", active ? "" : "hidden sm:inline")}>{label}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** Light header used by the /start/* screens; on the right the same profile pill as the app bar (with Log out) */
export function OnboardingHeader({ current }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 sm:h-[76px] items-center justify-between gap-4 border-b border-gray-200 bg-white px-4 sm:px-8 lg:px-14">
      {/* Narrow phones: app icon only, so the logo, stepper and profile pill fit on one line */}
      <BrandLogo variant="dark" className="[&>span]:hidden [&>span]:whitespace-nowrap min-[440px]:[&>span]:inline" />
      {current ? <OnboardingStepper current={current} /> : <span />}
      <OnboardingUserMenu />
    </header>
  );
}

export { CheckMark };
